import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DialogFooter } from "@/components/ui/dialog";
import {
  etfFormSchema,
  ETFFormValues,
  ETF_CATEGORIES,
  DIVIDEND_FREQUENCIES,
  SUPPORTED_CURRENCIES,
} from "./etfForm.schema";
import {ETFHolding} from "@/integrations/supabase/etf.models.ts";

interface ETFFormProps {
  holding?: ETFHolding;
  onSave: (data: ETFHoldingCreateRequest) => void | Promise<void>;
  onCancel: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  equity: "Equity",
  fixed_income: "Fixed Income",
  commodity: "Commodity",
  real_estate: "Real Estate",
  multi_asset: "Multi-Asset",
};

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  semi_annual: "Semi-Annual",
  annual: "Annual",
};

export function ETFForm({ holding, onSave, onCancel }: ETFFormProps) {
  const form = useForm<ETFFormValues>({
    resolver: zodResolver(etfFormSchema),
    defaultValues: {
      ticker_symbol: holding?.ticker_symbol ?? "",
      etf_name: holding?.etf_name ?? "",
      category: holding?.category as ETFCategory ?? undefined,
      units_held: holding?.units_held ?? undefined,
      average_cost_per_unit: holding?.average_cost_per_unit ?? undefined,
      currency: holding?.currency as typeof SUPPORTED_CURRENCIES[number] ?? undefined,
      purchase_date: holding?.purchase_date ?? "",
      dividend_per_unit: holding?.dividend_per_unit ?? undefined,
      dividend_frequency: holding?.dividend_frequency as ETFDividendFrequency ?? undefined,
      notes: holding?.notes ?? "",
    },
  });

  const onSubmit = async (values: ETFFormValues) => {
    await onSave(values as ETFHoldingCreateRequest);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        aria-label={holding ? "Edit ETF holding" : "Add ETF holding"}
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="ticker_symbol"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ticker Symbol *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g. SPY"
                    className="uppercase"
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="etf_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>ETF Name *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g. SPDR S&P 500 ETF"
                    aria-required="true"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger aria-required="true" aria-label="Select category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ETF_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {CATEGORY_LABELS[cat]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="units_held"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Units Held *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.000001"
                    min="0"
                    placeholder="e.g. 10.5"
                    aria-required="true"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? undefined : parseFloat(e.target.value)
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="average_cost_per_unit"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Avg Cost per Unit *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="e.g. 450.00"
                    aria-required="true"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? undefined : parseFloat(e.target.value)
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger aria-required="true" aria-label="Select currency">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {SUPPORTED_CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="purchase_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Purchase Date *</FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    aria-required="true"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="dividend_per_unit"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dividend per Unit</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="e.g. 1.50"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? undefined : parseFloat(e.target.value)
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dividend_frequency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dividend Frequency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? ""}
                >
                  <FormControl>
                    <SelectTrigger aria-label="Select dividend frequency">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {DIVIDEND_FREQUENCIES.map((freq) => (
                      <SelectItem key={freq} value={freq}>
                        {FREQUENCY_LABELS[freq]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Optional notes about this ETF holding"
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            {holding ? "Save Changes" : "Add ETF"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
